import { createSlice } from "@reduxjs/toolkit";

import {
  fetchAllChecklists,
  fetchChecklistBySlug,
  submitChecklistData,
  fetchSubmissionByDate,
  fetchChecklistDetail,
  fetchChecklistStats,
} from "./checkActions.js";

const initialState = {
  // All checklist templates
  allChecklists: [],

  // Individual checklists indexed by slug
  checklistsBySlug: {},

  // Current submission
  currentSubmission: null,

  // Submission records indexed by checklist and calendar date.
  submissionsByDate: {},

  // Checklist detail / summary
  checklistDetail: {
    data: null,
    loading: false,
    error: null,
  },

  // Checklist dashboard stats
  checklistStats: {
    data: null,
    loading: false,
    error: null,
  },

  loading: false,
  error: null,
};

const checkSlice = createSlice({
  name: "checklist",

  initialState,

  reducers: {
    clearChecklistErrors: (state) => {
      state.error = null;
      state.checklistDetail.error = null;
      state.checklistStats.error = null;
    },

    resetCurrentSubmission: (state) => {
      state.currentSubmission = null;
    },

    resetChecklistDetail: (state) => {
      state.checklistDetail = {
        data: null,
        loading: false,
        error: null,
      };
    },

    resetChecklistStats: (state) => {
      state.checklistStats = {
        data: null,
        loading: false,
        error: null,
      };
    },
  },

  extraReducers: (builder) => {
    builder

      // ============================================================
      // 1. FETCH ALL CHECKLISTS
      // ============================================================

      .addCase(fetchAllChecklists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchAllChecklists.fulfilled, (state, action) => {
        state.loading = false;

        state.allChecklists = action.payload.data || [];
      })

      .addCase(fetchAllChecklists.rejected, (state, action) => {
        state.loading = false;

        state.error =
          action.payload || "Failed to fetch checklists";
      })

      // ============================================================
      // 2. FETCH CHECKLIST TEMPLATE BY SLUG
      // ============================================================

      .addCase(fetchChecklistBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchChecklistBySlug.fulfilled, (state, action) => {
        state.loading = false;

        const dataPayload = action.payload.data;

        state.checklistsBySlug[dataPayload.slug] = dataPayload;
      })

      .addCase(fetchChecklistBySlug.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload;
      })

      // ============================================================
      // 3. FETCH EXISTING DAY SUBMISSION
      // ============================================================

      .addCase(fetchSubmissionByDate.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const { checklistId, creationDate } = action.meta.arg;
        const key = checklistId + ':' + creationDate;
        state.submissionsByDate[key] = {
          data: state.submissionsByDate[key]?.data || null,
          loading: true, error: null, requestId: action.meta.requestId,
        };
      })

      .addCase(fetchSubmissionByDate.fulfilled, (state, action) => {
        const { checklistId, creationDate } = action.meta.arg;
        const record = state.submissionsByDate[checklistId + ':' + creationDate];
        if (record?.requestId !== action.meta.requestId) return;
        record.loading = false;
        record.data = action.payload.exists ? action.payload.data : null;
        state.loading = false;

        state.currentSubmission = action.payload.exists
          ? action.payload.data
          : null;
      })

      .addCase(fetchSubmissionByDate.rejected, (state, action) => {
        const { checklistId, creationDate } = action.meta.arg;
        const record = state.submissionsByDate[checklistId + ':' + creationDate];
        if (record?.requestId !== action.meta.requestId) return;
        state.loading = false;
        record.loading = false;
        record.error = action.meta.aborted ? null : action.payload;
        state.error = record.error;
      })

      // ============================================================
      // 4. SUBMIT / UPDATE MAIN CHECKLIST
      // ============================================================

      .addCase(submitChecklistData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(submitChecklistData.fulfilled, (state, action) => {
        state.loading = false;

        state.currentSubmission = action.payload.data;
        const submission = action.payload.data;
        if (submission?.checklistId && submission?.creationDate) {
          const key = submission.checklistId + ':' + submission.creationDate;
          state.submissionsByDate[key] = {
            data: submission, loading: false, error: null, requestId: null,
          };
        }
      })

      .addCase(submitChecklistData.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload;
      })

      // ============================================================
      // 5. FETCH CHECKLIST DETAIL / SUMMARY
      // ============================================================

      .addCase(fetchChecklistDetail.pending, (state) => {
        state.checklistDetail.loading = true;
        state.checklistDetail.error = null;
      })

      .addCase(fetchChecklistDetail.fulfilled, (state, action) => {
        state.checklistDetail.loading = false;

        state.checklistDetail.data = action.payload.data;
      })

      .addCase(fetchChecklistDetail.rejected, (state, action) => {
        state.checklistDetail.loading = false;

        state.checklistDetail.error =
          action.payload || "Failed to fetch checklist detail";
      })

      // ============================================================
      // 6. FETCH CHECKLIST STATS
      // ============================================================

      .addCase(fetchChecklistStats.pending, (state, action) => {
        state.checklistStats.loading = true;
        state.checklistStats.error = null;
        state.checklistStats.data = null;
        state.checklistStats.requestId = action.meta.requestId;
      })

      .addCase(fetchChecklistStats.fulfilled, (state, action) => {
        if (state.checklistStats.requestId !== action.meta.requestId) return;
        state.checklistStats.loading = false;
        state.checklistStats.error = null;

        state.checklistStats.data = action.payload.data || null;
      })

      .addCase(fetchChecklistStats.rejected, (state, action) => {
        if (state.checklistStats.requestId !== action.meta.requestId) return;
        state.checklistStats.loading = false;

        state.checklistStats.error =
          action.meta.aborted ? null : action.payload || "Failed to fetch checklist statistics";
      });
  },
});

export const {
  clearChecklistErrors,
  resetCurrentSubmission,
  resetChecklistDetail,
  resetChecklistStats,
} = checkSlice.actions;

export default checkSlice.reducer;
